import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  CHECKLIST_PHASES,
  pageSchema,
  serviceItemRowSchema,
  type AdminTheatre,
  type ChecklistItemView,
  type ChecklistPhase,
} from '@wodi/shared';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  PageHeader,
  Section,
  Select,
  Tag,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { api } from '../api/client.ts';
import { can, sessionQuery } from '../api/session.ts';
import {
  adminTheatresQuery,
  checklistItemsQuery,
  createChecklistItem,
  createTheatre,
  updateChecklistItem,
  updateTheatre,
} from '../api/theatre.ts';
import { useOnline } from '../hooks/use-online.ts';
import { SettingsPage } from '../routes/settings-nav.tsx';
import { toasts } from '../toasts.ts';
import styles from './theatre.module.css';
import { errorText, PHASE_WORDS } from './words.ts';

// Settings > Theatres (docs/plans/phase2-theatre.md §6): theatres and the service their fee is
// charged as, and the safety checklist items for each step. The items are the facility's own
// wording (T1): seeded ones are placeholders until the clinical lead saves their wording.

function useRefresh() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin'] });
    await queryClient.invalidateQueries({ queryKey: ['theatre'] });
  };
}

/** Active services a theatre fee can be charged as (procedure or other). */
function useFeeItems() {
  return useQuery({
    queryKey: ['catalogue', 'theatre-fee-items'],
    queryFn: async () => {
      const [procedures, other] = await Promise.all(
        ['procedure', 'other'].map((category) =>
          api('GET', `/catalogue/items?category=${category}&status=active&limit=100`, {
            schema: pageSchema(serviceItemRowSchema),
          }),
        ),
      );
      return [...(other?.items ?? []), ...(procedures?.items ?? [])].filter(
        (i) => i.kind === 'service',
      );
    },
  });
}

function TheatreRow({ theatre }: { theatre: AdminTheatre }) {
  const online = useOnline();
  const refresh = useRefresh();
  const toggle = useMutation({
    mutationFn: () =>
      updateTheatre(theatre.id, { status: theatre.status === 'active' ? 'inactive' : 'active' }),
    onSuccess: async (t) => {
      await refresh();
      toasts.show({
        title: t.status === 'active' ? 'Theatre switched on' : 'Theatre switched off',
        description: t.name,
      });
    },
  });
  return (
    <li className={styles.phaseItem}>
      <span>
        {theatre.name}
        <span
          className={styles.meta}
        >{` Fee: ${theatre.feeItem.name} (${theatre.feeItem.code})`}</span>
      </span>
      <span className={styles.actions}>
        <Tag state={theatre.status === 'active' ? 'done' : 'neutral'}>
          {theatre.status === 'active' ? 'In use' : 'Switched off'}
        </Tag>
        <Button
          dense
          isDisabled={!online}
          isPending={toggle.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            toggle.mutate();
          }}
        >
          {theatre.status === 'active' ? 'Switch off' : 'Switch on'}
        </Button>
      </span>
    </li>
  );
}

function AddTheatre() {
  const online = useOnline();
  const refresh = useRefresh();
  const items = useFeeItems();
  const [name, setName] = useState('');
  const [itemId, setItemId] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: () => createTheatre({ name: name.trim(), feeServiceItemId: itemId ?? '' }),
    onSuccess: async (t) => {
      await refresh();
      toasts.show({ title: 'Theatre added', description: t.name });
      setName('');
      setItemId(null);
    },
  });
  return (
    <form
      className={styles.form}
      aria-label="Add a theatre"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim() !== '' && itemId !== null) add.mutate();
      }}
    >
      <div className={styles.row}>
        <TextField label="Theatre name" value={name} onChange={setName} />
        <Select
          label="Theatre fee charged as"
          placeholder={items.isPending ? 'Loading…' : 'Choose a service'}
          description="A service in the procedure or other category, priced in the price lists."
          options={(items.data ?? []).map((i) => ({ id: i.id, label: `${i.name} (${i.code})` }))}
          value={itemId}
          onChange={(v) => {
            setItemId(typeof v === 'string' ? v : null);
          }}
        />
      </div>
      {add.error === null ? null : (
        <Notice tone="error">{errorText(add.error, "The theatre wasn't added. Try again.")}</Notice>
      )}
      <div className={styles.actions}>
        <Button
          type="submit"
          isDisabled={!online || name.trim() === '' || itemId === null}
          isPending={add.isPending}
          pendingLabel="Adding…"
        >
          Add theatre
        </Button>
      </div>
    </form>
  );
}

function EditItemDialog({ item, onClose }: { item: ChecklistItemView; onClose: () => void }) {
  const online = useOnline();
  const refresh = useRefresh();
  const [text, setText] = useState(item.text);
  const [requiresYes, setRequiresYes] = useState(item.requiresYes);
  const save = useMutation({
    mutationFn: () =>
      updateChecklistItem(item.id, {
        ...(text.trim() !== item.text || item.provisional ? { text: text.trim() } : {}),
        requiresYes,
      }),
    onSuccess: async () => {
      await refresh();
      toasts.show({ title: 'Checklist item saved' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Edit checklist item"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <TextArea
        label="Wording"
        description="As your clinical lead approved it. Saving confirms the wording."
        value={text}
        onChange={setText}
        rows={3}
      />
      <Checkbox isSelected={requiresYes} onChange={setRequiresYes}>
        Must be Yes to go on
      </Checkbox>
      {save.error === null ? null : (
        <Notice tone="error">{errorText(save.error, "The item wasn't saved. Try again.")}</Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!online || text.trim() === '' || text.trim().length > 300}
          isPending={save.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            save.mutate();
          }}
        >
          Save item
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PhaseItems({ phase, items }: { phase: ChecklistPhase; items: ChecklistItemView[] }) {
  const online = useOnline();
  const refresh = useRefresh();
  const [editing, setEditing] = useState<ChecklistItemView | null>(null);
  const [text, setText] = useState('');
  const add = useMutation({
    mutationFn: () =>
      createChecklistItem({
        phase,
        text: text.trim(),
        sortOrder: (items.at(-1)?.sortOrder ?? 0) + 10,
      }),
    onSuccess: async () => {
      await refresh();
      toasts.show({ title: 'Checklist item added', description: PHASE_WORDS[phase] });
      setText('');
    },
  });
  const toggle = useMutation({
    mutationFn: (item: ChecklistItemView) =>
      updateChecklistItem(item.id, { status: item.status === 'active' ? 'inactive' : 'active' }),
    onSuccess: refresh,
  });
  return (
    <Section title={PHASE_WORDS[phase]} headingLevel={3}>
      <ul className={styles.phaseList} aria-label={`${PHASE_WORDS[phase]} items`}>
        {items.map((item) => (
          <li key={item.id} className={styles.phaseItem}>
            <span>{item.text}</span>
            <span className={styles.actions}>
              {item.provisional ? <Tag state="attention">Placeholder</Tag> : null}
              {item.requiresYes ? <Tag state="active">Must be Yes</Tag> : null}
              {item.status === 'inactive' ? <Tag state="neutral">Switched off</Tag> : null}
              <Button
                dense
                isDisabled={!online}
                onPress={() => {
                  setEditing(item);
                }}
              >
                Edit
              </Button>
              <Button
                dense
                variant="quiet"
                isDisabled={!online}
                onPress={() => {
                  toggle.mutate(item);
                }}
              >
                {item.status === 'active' ? 'Switch off' : 'Switch on'}
              </Button>
            </span>
          </li>
        ))}
      </ul>
      <form
        className={styles.row}
        aria-label={`Add a ${PHASE_WORDS[phase]} item`}
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim() !== '') add.mutate();
        }}
      >
        <TextField label="New item wording" value={text} onChange={setText} />
        <div className={styles.actions}>
          <Button
            type="submit"
            isDisabled={!online || text.trim() === ''}
            isPending={add.isPending}
            pendingLabel="Adding…"
          >
            Add item
          </Button>
        </div>
      </form>
      {editing === null ? null : (
        <EditItemDialog
          item={editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </Section>
  );
}

export function TheatresSettingsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const theatres = useQuery(adminTheatresQuery);
  const items = useQuery(checklistItemsQuery);
  if (session === null || !can(session, 'theatre.settings.manage'))
    return (
      <>
        <PageHeader title="Theatres" />
        <Notice tone="info">
          You don't have permission to set up theatres. Ask an administrator if you need it.
        </Notice>
      </>
    );
  const anyPlaceholder = (items.data ?? []).some((i) => i.provisional && i.status === 'active');
  return (
    <SettingsPage title="Theatres">
      <Section title="Theatres">
        {theatres.isPending ? <LoadingBlock label="Loading theatres" rows={2} /> : null}
        {theatres.isError ? (
          <Notice
            tone="error"
            action={
              <Button dense onPress={() => void theatres.refetch()}>
                Try again
              </Button>
            }
          >
            {errorText(theatres.error, "Theatres couldn't be loaded. Try again.")}
          </Notice>
        ) : null}
        {theatres.isSuccess && theatres.data.length === 0 ? (
          <p className={styles.meta}>No theatres yet. Add the first one below.</p>
        ) : null}
        <ul className={styles.phaseList} aria-label="Theatres">
          {(theatres.data ?? []).map((t) => (
            <TheatreRow key={t.id} theatre={t} />
          ))}
        </ul>
        <AddTheatre />
      </Section>
      <Section
        title="Safety checklist"
        description="The pre-op checklist and the WHO steps, in your facility's approved wording."
      >
        {anyPlaceholder ? (
          <Notice tone="info">
            Some items are placeholders. The clinical lead replaces them with the facility's
            approved wording before theatre staff rely on the checklist.
          </Notice>
        ) : null}
        {items.isPending ? <LoadingBlock label="Loading the checklist" rows={4} /> : null}
        {items.isError ? (
          <Notice tone="error">
            {errorText(items.error, "The checklist couldn't be loaded. Try again.")}
          </Notice>
        ) : null}
        {items.isSuccess
          ? CHECKLIST_PHASES.map((phase) => (
              <PhaseItems
                key={phase}
                phase={phase}
                items={items.data.filter((i) => i.phase === phase)}
              />
            ))
          : null}
      </Section>
    </SettingsPage>
  );
}
